import * as React from "react";
import { Card as V2Card, CardContent as V2CardContent } from "../atoms/card.js";
import { Typography } from "../atoms/typography.js";
import { cx } from "../internal/cx.js";

/**
 * StatementCard — a compact, read-only benefits/cost statement: a header with
 * the title (+ optional muted glyph) and an inline action on the right, a plain
 * period + total summary line, and one clean row per line item (leading icon ·
 * name with a supporting subline · right-aligned amount).
 *
 * This is the flat icon-row statement used on the Benefits Wallet overview
 * (beside Personal Information). It is deliberately distinct from
 * `CompactBenefitsStatement`, which is the expandable, zebra-striped
 * data-table statement for multi-employee billing periods (Owner Dashboard).
 * Reach for StatementCard when every line is a top-level row with its own
 * product glyph and there is nothing to expand.
 *
 * Purely presentational: amounts and dates are pre-formatted by the caller;
 * the icon is a caller-supplied slot so the component stays domain-free.
 */

export interface StatementCardRow {
  /** Stable key. */
  id: string;
  /** Leading visual — typically a `<ProductIcon>` from `@cakewalk/ui`. */
  icon?: React.ReactNode;
  /** Line-item name ("Local Dental Insurance"). */
  name: React.ReactNode;
  /** Supporting subline under the name ("Effective May 21, 2026 · Employee Only"). */
  subline?: React.ReactNode;
  /** Pre-formatted amount ("$35.00"). */
  amount: React.ReactNode;
  /** Suffix after the amount, rendered lighter ("/mo"). */
  amountSuffix?: React.ReactNode;
}

export interface StatementCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** Card heading. Default "Your benefits statement". */
  title?: React.ReactNode;
  /** Leading glyph before the title, rendered plain + muted (no tile). Omit for no icon. */
  titleIcon?: React.ReactNode | null;
  /** Action rendered inline on the right of the header (e.g. a "See full statement" link Button). */
  action?: React.ReactNode;
  /** Statement period ("July 1, 2026 – July 31, 2026"). */
  period?: React.ReactNode;
  /** Label prefix for the period line. Default "Period". */
  periodLabel?: React.ReactNode;
  /** Pre-formatted total across all rows ("$122.00"). */
  totalAmount?: React.ReactNode;
  /** Label for the total line. Default "Total monthly". */
  totalLabel?: React.ReactNode;
  /** Line-item rows. */
  rows: StatementCardRow[];
}

export const StatementCard = React.forwardRef<HTMLDivElement, StatementCardProps>(
  function StatementCard(
    {
      action,
      className,
      period,
      periodLabel = "Period",
      rows,
      title = "Your benefits statement",
      titleIcon,
      totalAmount,
      totalLabel = "Total monthly",
      ...props
    },
    ref,
  ) {
    return (
      <V2Card
        ref={ref}
        variant="section"
        className={cx("cw-v2-statement-card", className)}
        {...props}
      >
        <V2CardContent className="cw-v2-statement-card__content">
          {/* Header: title (+ leading icon) with the action inline on the right. */}
          <div className="cw-v2-statement-card__header">
            <div className="cw-v2-statement-card__title-group">
              {titleIcon ? (
                <span className="cw-v2-statement-card__title-icon" aria-hidden>
                  {titleIcon}
                </span>
              ) : null}
              <Typography variant="heading-medium" as="h2" className="cw-v2-statement-card__title">
                {title}
              </Typography>
            </div>
            {action ? <div className="cw-v2-statement-card__action">{action}</div> : null}
          </div>

          {/* Summary: plain inline period + total, no boxed band. */}
          {period || totalAmount ? (
            <div className="cw-v2-statement-card__summary">
              {period ? (
                <Typography variant="caption" muted>
                  {periodLabel}: {period}
                </Typography>
              ) : null}
              {totalAmount ? (
                <div className="cw-v2-statement-card__summary-total">
                  <Typography variant="caption" muted>
                    {totalLabel}
                  </Typography>
                  <Typography variant="heading-small" as="span">
                    {totalAmount}
                  </Typography>
                </div>
              ) : null}
            </div>
          ) : null}

          <ul className="cw-v2-statement-card__rows">
            {rows.map((row) => (
              <li key={row.id} className="cw-v2-statement-card__row">
                {row.icon ? (
                  <span className="cw-v2-statement-card__row-icon" aria-hidden>
                    {row.icon}
                  </span>
                ) : null}
                <div className="cw-v2-statement-card__row-body">
                  <Typography variant="body" className="cw-v2-statement-card__row-name">
                    {row.name}
                  </Typography>
                  {row.subline ? (
                    <Typography
                      variant="caption"
                      muted
                      className="cw-v2-statement-card__row-subline"
                    >
                      {row.subline}
                    </Typography>
                  ) : null}
                </div>
                <Typography variant="body" className="cw-v2-statement-card__row-amount">
                  {row.amount}
                  {row.amountSuffix ? (
                    <span className="cw-v2-statement-card__row-amount-suffix">
                      {row.amountSuffix}
                    </span>
                  ) : null}
                </Typography>
              </li>
            ))}
          </ul>
        </V2CardContent>
      </V2Card>
    );
  },
);
StatementCard.displayName = "StatementCard";
